import React, {useEffect, useRef, useState} from "react";
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {useSnackbar} from "notistack";
import "react-checkbox-tree/lib/react-checkbox-tree.css";
import CheckboxTree from "react-checkbox-tree";
import {useGetMediaTreeQuery} from "../../drafts/api/draftsApiSlice";
import {useGetWorkspaceGroupsQuery} from "../../creations/api/creationsApiSlice";
import {
    useCreateMediumAccessMutation,
    useDeleteMediumAccessMutation,
    useGetAccessUsersQuery,
    useGetMediumAccessesQuery,
    useUpdateMediumAccessMutation
} from "../api/accessManagementApiSlice";
import {useGetCmEnumsQuery} from "../../../app/appApiSlice";
import {MediaNode} from "../../drafts/types";
import {MediumAssignment, PrincipalType} from "../types";
import {getUuid} from "../../../config/general";
import useDebounce from "../../../hooks/useDebounce";
import EditMediumsModal from "./EditMediumsModal";
import "./AccessMediums.scss";

const AccessMediums: React.FC = () => {
    const {enqueueSnackbar} = useSnackbar();

    const [principalType, setPrincipalType] = useState<PrincipalType>('User');
    const [selectedPrincipalId, setSelectedPrincipalId] = useState<number | null>(null);

    const [mediaFormat, setMediaFormat] = useState<string>('');
    const [openMediaTreeModal, setOpenMediaTreeModal] = useState(false);
    const [nodes, setNodes] = useState<any[]>([]);
    const [filteredNodes, setFilteredNodes] = useState<any[]>([]);
    const [checkedNodes, setCheckedNodes] = useState<string[]>([]);
    const [expanded, setExpanded] = useState<string[]>([]);
    const [treeState, setTreeState] = useState({key: Math.random()});
    const [filterNodesText, setFilterNodesText] = useState('');
    const [mediaLength, setMediaLength] = useState<number | null>(null);

    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingAssignment, setEditingAssignment] = useState<MediumAssignment | null>(null);
    const [openEditModal, setOpenEditModal] = useState(false);

    const checkboxTreeRef = useRef<any>();

    const {data: users, isLoading: loadingUsers} = useGetAccessUsersQuery();
    const {data: groups, isLoading: loadingGroups} = useGetWorkspaceGroupsQuery();
    const {data: assignments = []} = useGetMediumAccessesQuery();
    const [createMediumAccess, {isLoading: creatingAssignment}] = useCreateMediumAccessMutation();
    const [updateMediumAccess] = useUpdateMediumAccessMutation();
    const [deleteMediumAccess] = useDeleteMediumAccessMutation();
    const {data: cmEnums} = useGetCmEnumsQuery();
    const {
        currentData: mediaTree,
        isFetching: loadingMediaTree
    } = useGetMediaTreeQuery(mediaFormat || '', {skip: !mediaFormat});

    let mediaSize = 0;
    let flattenNodeValues: string[] = [];

    const buildMediaTreeCheckboxNodes: any = (tree: MediaNode[]) => {
        return tree.map((element: MediaNode, index) => {
            if (element.children && element.children.length > 0) {
                let val = element.id + '_' + element.type + getUuid(index);
                let place = '';

                if (element.type === 'Media::Agglomeration') {
                    place = '(Aglomeracja)';
                } else if (element.type === 'Media::City') {
                    place = '(Miasto)';
                }

                return {
                    value: val,
                    label: element.name + ' ' + `${place}`,
                    children: buildMediaTreeCheckboxNodes(element.children),
                };
            } else {
                let val = element.id + '_' + element.type + getUuid(index);
                mediaSize += 1;
                flattenNodeValues.push(val);
                return {
                    value: val,
                    label: element.asId ? element.asId + ' - ' + element.name : element.name,
                };
            }
        });
    };

    useEffect(() => {
        if (mediaTree) {
            mediaSize = 0;
            const mediaTreeCheckboxNodes = buildMediaTreeCheckboxNodes(mediaTree);
            setTimeout(() => {
                setNodes(mediaTreeCheckboxNodes);
                setFilteredNodes([{
                    label: 'Wszystkie nośniki',
                    value: 'all',
                    children: mediaTreeCheckboxNodes
                }]);
                setMediaLength(mediaSize);
            }, 200);
        }
    }, [mediaTree]);

    const onFilterNodeChange = (event: any) => {
        setFilterNodesText(event.target.value);
    };

    const filterNodes = (filtered: any, node: any) => {
        if (node.label.toLocaleLowerCase().match(filterNodesText.toString().toLocaleLowerCase())) {
            filtered.push(node);
            return filtered;
        }
        if (node.children && node.children.length > 0) {
            const children = (node.children || []).reduce(filterNodes, []);
            if (children.length > 0) {
                filtered.push({...node, children});
            }
        }
        return filtered;
    };

    useDebounce(() => {
        if (!filterNodesText) {
            setFilteredNodes([{
                label: 'Wszystkie nośniki',
                value: 'all',
                children: nodes
            }]);
            return;
        }
        setFilteredNodes([{
            label: 'Wszystkie nośniki',
            value: 'all',
            children: nodes.reduce(filterNodes, [])
        }]);
        checkboxTreeRef.current?.onExpandAll();
    }, [filterNodesText], 100);

    const handleOpenNodesModal = () => setOpenMediaTreeModal(true);
    const handleCloseNodesModal = () => setOpenMediaTreeModal(false);

    const checkedMediaIds = checkedNodes
        .filter(el => el !== 'all')
        .map((node: string) => parseInt(node.split('_')[0]))
        .filter(id => !isNaN(id));
    const uniqueCheckedMediaIds = Array.from(new Set(checkedMediaIds));

    const selectedPrincipalLabel = principalType === 'User'
        ? users?.find(u => u.id === selectedPrincipalId)?.email
        : groups?.find(g => g.id === selectedPrincipalId)?.name;

    const canAssign = selectedPrincipalId != null && uniqueCheckedMediaIds.length > 0;

    const resetCreateMediaState = () => {
        setMediaFormat('');
        setCheckedNodes([]);
        setNodes([]);
        setFilteredNodes([]);
        setFilterNodesText('');
        setMediaLength(null);
    };

    const handleAssign = async () => {
        if (!canAssign || selectedPrincipalId == null || !selectedPrincipalLabel) {
            return;
        }

        try {
            await createMediumAccess({
                principalType,
                principalId: selectedPrincipalId,
                mediaFormat,
                mediaIds: uniqueCheckedMediaIds,
            }).unwrap();
            enqueueSnackbar('Dodano przypisanie nośników', {variant: 'success'});

            setSelectedPrincipalId(null);
            resetCreateMediaState();
        } catch (e) {
            enqueueSnackbar('Nie udało się dodać przypisania nośników', {variant: 'error'});
        }
    };

    const handleOpenEditModal = (assignment: MediumAssignment) => {
        setEditingId(assignment.id);
        setEditingAssignment(assignment);
        setOpenEditModal(true);
    };

    const handleCloseEditModal = () => {
        setOpenEditModal(false);
        setEditingId(null);
        setEditingAssignment(null);
    };

    const handleSaveEdit = async (newMediaFormat: string, newMediaIds: number[]) => {
        if (editingId == null || editingAssignment == null) {
            return;
        }

        try {
            await updateMediumAccess({
                id: editingId,
                principalType: editingAssignment.principalType,
                principalId: editingAssignment.principalId,
                mediaFormat: newMediaFormat,
                mediaIds: newMediaIds,
            }).unwrap();
            enqueueSnackbar('Zaktualizowano przypisanie nośników', {variant: 'success'});

            handleCloseEditModal();
        } catch (e) {
            enqueueSnackbar('Nie udało się zaktualizować przypisania nośników', {variant: 'error'});
        }
    };

    const handleRemoveAssignment = async (id: number) => {
        try {
            await deleteMediumAccess(id).unwrap();
            enqueueSnackbar('Usunięto przypisanie nośników', {variant: 'success'});
        } catch (e) {
            enqueueSnackbar('Nie udało się usunąć przypisania nośników', {variant: 'error'});
        }
    };

    const handlePrincipalTypeChange = (value: PrincipalType) => {
        setPrincipalType(value);
        setSelectedPrincipalId(null);
    };

    return (
        <Box className={'AccessMediums'}>
            <Box className={'page-header'}>
                <h1>Nośniki</h1>
                <p>Przypisz nośniki do użytkownika lub grupy</p>
            </Box>

            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <FormControl>
                        <RadioGroup
                            row
                            value={principalType}
                            onChange={(_, value) => handlePrincipalTypeChange(value as PrincipalType)}
                        >
                            <FormControlLabel value="User" control={<Radio/>} label="Użytkownik"/>
                            <FormControlLabel value="Group" control={<Radio/>} label="Grupa"/>
                        </RadioGroup>
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                    {principalType === 'User' ? (
                        <Autocomplete
                            fullWidth
                            size="small"
                            loading={loadingUsers}
                            options={users || []}
                            getOptionLabel={(option) => option.email}
                            value={users?.find(u => u.id === selectedPrincipalId) || null}
                            onChange={(_, value) => setSelectedPrincipalId(value ? value.id : null)}
                            renderInput={(params) => <TextField {...params} label="Użytkownik"/>}
                        />
                    ) : (
                        <Autocomplete
                            fullWidth
                            size="small"
                            loading={loadingGroups}
                            options={groups || []}
                            getOptionLabel={(option) => option.name}
                            value={groups?.find(g => g.id === selectedPrincipalId) || null}
                            onChange={(_, value) => setSelectedPrincipalId(value ? value.id : null)}
                            renderInput={(params) => <TextField {...params} label="Grupa"/>}
                        />
                    )}
                </Grid>

                <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                        <InputLabel shrink id="media-format-select-label">Format nośnika</InputLabel>
                        <Select
                            variant="outlined"
                            labelId="media-format-select-label"
                            id="media-format-select"
                            label="Format nośnika"
                            value={mediaFormat}
                            notched
                            onChange={(event) => {
                                setCheckedNodes([]);
                                setNodes([]);
                                setFilteredNodes([]);
                                setMediaLength(null);
                                setMediaFormat(event.target.value);
                            }}
                        >
                            <MenuItem value="">
                                <em>Brak</em>
                            </MenuItem>
                            {cmEnums && (cmEnums.find(cmEnum => cmEnum.name === 'mediaFormats')?.values as string[]).map((format) => (
                                <MenuItem key={format} value={format}>{format}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12}>
                    <Button disabled={loadingMediaTree || !mediaFormat} variant="contained" onClick={handleOpenNodesModal}>
                        Wybierz nośniki&nbsp;
                        {loadingMediaTree && mediaFormat
                            ? <CircularProgress sx={{marginLeft: '10px'}} size={20}/>
                            : `${uniqueCheckedMediaIds.length}/${mediaLength || 0}`}
                    </Button>
                </Grid>

                <Grid item xs={12}>
                    <Button
                        variant="contained"
                        disabled={!canAssign || creatingAssignment}
                        onClick={handleAssign}
                    >
                        Przypisz nośniki
                    </Button>
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="h6" sx={{mb: 1}}>Przypisania</Typography>
                    <TableContainer component={Paper}>
                        <Table aria-label="Tabela przypisań nośników">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Typ</TableCell>
                                    <TableCell>Nazwa</TableCell>
                                    <TableCell>Format nośnika</TableCell>
                                    <TableCell align="right">Liczba nośników</TableCell>
                                    <TableCell align="right">Akcje</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {assignments.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">
                                            <Typography color="text.secondary">Brak przypisań</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {assignments.map((assignment) => (
                                    <TableRow key={assignment.id} selected={editingId === assignment.id}>
                                        <TableCell>{assignment.principalType === 'User' ? 'Użytkownik' : 'Grupa'}</TableCell>
                                        <TableCell>{assignment.principalLabel}</TableCell>
                                        <TableCell>{assignment.mediaFormat}</TableCell>
                                        <TableCell align="right">{assignment.mediaIds.length}</TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                aria-label="Edytuj przypisanie"
                                                color="primary"
                                                onClick={() => handleOpenEditModal(assignment)}
                                            >
                                                <EditIcon/>
                                            </IconButton>
                                            <IconButton
                                                aria-label="Usuń przypisanie"
                                                color="error"
                                                onClick={() => handleRemoveAssignment(assignment.id)}
                                            >
                                                <DeleteIcon/>
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>

            <Dialog
                open={openMediaTreeModal}
                onClose={handleCloseNodesModal}
                fullWidth={true}
                maxWidth="md"
            >
                <DialogTitle id="alert-dialog-title">
                    Wybierz nośniki
                </DialogTitle>
                <DialogContent>
                    {loadingMediaTree && (
                        <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px'}}>
                            <CircularProgress size={100}/>
                        </Box>
                    )}

                    <Box sx={{minHeight: '300px'}}>
                        {mediaTree && (
                            <>
                                <TextField
                                    value={filterNodesText}
                                    label="Wyszukaj..."
                                    onInput={onFilterNodeChange}
                                    variant="outlined"
                                    sx={{mt: 1, mb: 3}}
                                    fullWidth
                                />
                                <CheckboxTree
                                    ref={checkboxTreeRef}
                                    key={treeState.key}
                                    showNodeTitles={true}
                                    nodes={filteredNodes}
                                    checked={checkedNodes}
                                    expanded={expanded}
                                    showExpandAll={true}
                                    onCheck={(checked: any) => {
                                        setCheckedNodes(checked);
                                        setTreeState({key: Math.random()});
                                    }}
                                    onExpand={(exp: any) => {
                                        setExpanded(exp);
                                    }}
                                />
                            </>
                        )}
                        {mediaTree && mediaTree.length === 0 && (
                            <Typography color="text.secondary">Brak dostępnych nośników</Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseNodesModal}>Zamknij</Button>
                </DialogActions>
            </Dialog>

            <EditMediumsModal
                open={openEditModal}
                assignment={editingAssignment}
                onClose={handleCloseEditModal}
                onSave={handleSaveEdit}
            />
        </Box>
    );
};

export default AccessMediums;
